import { useEffect, useState } from 'react';
import {
  executePayrollRun,
  finalizePayrollRun,
  publishPayrollRun,
  listAllPayslips,
  getMyPayslips,
  getPayslip,
  getPayrollRun,
  listPayrollRuns,
  sharePayslip,
  recordPayslipDownload,
  submitTaxDeclaration,
  listDepartments,
  type PayrollRun,
  type PayslipDetail,
  type PayslipSummary,
  type Department,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import { StatusBadge } from '../components/StatusBadge';
import { Icon } from '../components/Icon';

function formatAmountInWords(amount: number, currency: string = 'USD'): string {
  if (amount === 0) return 'Zero ' + currency;
  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convertChunk(num: number): string {
    let str = '';
    if (num >= 100) {
      str += units[Math.floor(num / 100)] + ' Hundred ';
      num %= 100;
    }
    if (num >= 20) {
      str += tens[Math.floor(num / 10)] + ' ';
      num %= 10;
    }
    if (num >= 10) {
      str += teens[num - 10] + ' ';
      num = 0;
    }
    if (num > 0) {
      str += units[num] + ' ';
    }
    return str.trim();
  }

  let words = '';
  const millions = Math.floor(amount / 1000000);
  const thousands = Math.floor((amount % 1000000) / 1000);
  const remainder = Math.floor(amount % 1000);

  if (millions > 0) words += convertChunk(millions) + ' Million ';
  if (thousands > 0) words += convertChunk(thousands) + ' Thousand ';
  if (remainder > 0) words += convertChunk(remainder) + ' ';

  const currName = currency === 'USD' ? 'US Dollars' : currency === 'INR' ? 'Rupees' : currency === 'GBP' ? 'Pounds' : currency === 'AED' ? 'Dirhams' : currency === 'SGD' ? 'Singapore Dollars' : currency;
  return `${words.trim()} ${currName} Only`;
}

export function Payroll() {
  const { session, can } = useAuth();
  const isHRorAdmin = can('compensation.manage') || can('payroll.run') || can('payroll.view') || can('organization.manage');
  const { formatMoney, jurisdiction, country } = useLocalization();
  const [tab, setTab] = useState<'mypayslips' | 'runs' | 'directory' | 'declarations'>('mypayslips');
  const [myPayslips, setMyPayslips] = useState<PayslipSummary[]>([]);
  const [runs, setRuns] = useState<PayrollRun[]>([]);
  const [selectedRun, setSelectedRun] = useState<PayrollRun | null>(null);
  const [selectedRunPayslips, setSelectedRunPayslips] = useState<PayslipSummary[]>([]);
  const [directoryPayslips, setDirectoryPayslips] = useState<PayslipSummary[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Filters for My Payslips
  const [myFyFilter, setMyFyFilter] = useState('all');

  // Filters for Directory
  const [dirSearch, setDirSearch] = useState('');
  const [dirDept, setDirDept] = useState('');
  const [dirStatus, setDirStatus] = useState('all');
  const [dirMonth, setDirMonth] = useState('');

  // Execute Run Modal
  const [runModal, setRunModal] = useState(false);
  const [runMonth, setRunMonth] = useState('2026-08');
  const [processingRun, setProcessingRun] = useState(false);

  // Payslip Detail Modal
  const [viewingSlipId, setViewingSlipId] = useState<string | null>(null);
  const [slipDetail, setSlipDetail] = useState<PayslipDetail | null>(null);
  const [loadingSlip, setLoadingSlip] = useState(false);
  const [sharing, setSharing] = useState(false);

  function closePayslip() {
    setViewingSlipId(null);
    setSlipDetail(null);
  }


  // Tax Declaration state
  const [fy, setFy] = useState('2026-2027');
  const [regime, setRegime] = useState<'new' | 'old'>('new');
  const [sec80c, setSec80c] = useState(150000);
  const [sec80d, setSec80d] = useState(25000);
  const [rentPaid, setRentPaid] = useState(180000);
  const [otherExemptions, setOtherExemptions] = useState(0);

  async function loadData() {
    try {
      setLoading(true);
      const promises: Promise<any>[] = [
        getMyPayslips(myFyFilter !== 'all' ? { financialYear: myFyFilter } : undefined).catch(() => ({ data: [] })),
        listDepartments().catch(() => ({ data: [] })),
      ];
      if (isHRorAdmin) {
        promises.push(listPayrollRuns().catch(() => ({ data: [] })));
        promises.push(
          listAllPayslips({
            financialYear: myFyFilter !== 'all' ? myFyFilter : undefined,
            month: dirMonth || undefined,
            departmentId: dirDept || undefined,
            status: dirStatus !== 'all' ? dirStatus : undefined,
            search: dirSearch || undefined,
          }).catch(() => ({ data: [] }))
        );
      }
      const [mySlipsRes, deptsRes, runsRes, dirSlipsRes] = await Promise.all(promises);
      setMyPayslips(mySlipsRes.data || []);
      setDepartments(deptsRes.data || []);
      if (runsRes) setRuns(runsRes.data || []);
      if (dirSlipsRes) setDirectoryPayslips(dirSlipsRes.data || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load payroll data' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData();
  }, [myFyFilter, dirMonth, dirDept, dirStatus, dirSearch]);

  async function handleExecuteRun(e: React.FormEvent) {
    e.preventDefault();
    setProcessingRun(true);
    setMsg(null);
    try {
      const res = await executePayrollRun({
        month: runMonth,
        organizationId: session?.tenant.id || 'default-org',
      });
      setMsg({ type: 'success', text: `Payroll for ${runMonth} generated in draft state. Review calculations below.` });
      setRunModal(false);
      await loadData();
      if (res.data?.run) {
        setSelectedRun(res.data.run);
        setSelectedRunPayslips(res.data.payslips || []);
      }
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Payroll processing failed' });
    } finally {
      setProcessingRun(false);
    }
  }

  async function handleFinalize(runId: string) {
    if (!confirm('Are you sure you want to finalize and lock this payroll run?')) return;
    try {
      await finalizePayrollRun(runId);
      setMsg({ type: 'success', text: 'Payroll run approved and finalized! Ready for publication.' });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to finalize payroll run' });
    }
  }

  async function handlePublishRun(runId: string) {
    if (!confirm('Publish payslips to all employee self-service portals now?')) return;
    try {
      await publishPayrollRun(runId);
      setMsg({ type: 'success', text: '📢 Payslips published to employees! Staff can now view and download.' });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to publish payroll run' });
    }
  }

  async function openRunDetails(run: PayrollRun) {
    try {
      setSelectedRun(run);
      const res = await getPayrollRun(run.id);
      setSelectedRunPayslips(res.data?.payslips || []);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load run details' });
    }
  }

  async function openPayslip(id: string) {
    setViewingSlipId(id);
    setLoadingSlip(true);
    try {
      const res = await getPayslip(id);
      setSlipDetail(res.data);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to fetch payslip details' });
      setViewingSlipId(null);
    } finally {
      setLoadingSlip(false);
    }
  }

  async function handlePrintAndDownload(slipId: string) {
    try {
      await recordPayslipDownload(slipId);
      window.print();
    } catch (err: any) {
      console.error('Download logging error:', err);
      window.print();
    }
  }

  async function handleShareSlip(slipId: string) {
    setSharing(true);
    try {
      await sharePayslip(slipId);
      setMsg({ type: 'success', text: 'Payslip delivery notification dispatched to employee.' });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to dispatch payslip notification' });
    } finally {
      setSharing(false);
    }
  }

  async function handleSaveTax(e: React.FormEvent) {
    e.preventDefault();
    try {
      await submitTaxDeclaration({
        financialYear: fy,
        regime,
        section80cAmount: Number(sec80c),
        section80dAmount: Number(sec80d),
        hraRentPaidAnnual: Number(rentPaid),
        otherExemptions: Number(otherExemptions),
      });
      setMsg({ type: 'success', text: 'Income Tax Declaration saved successfully!' });
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to save tax declaration' });
    }
  }

  // Calculated metrics for My Payslips
  const totalMyNet = myPayslips.reduce((acc, s) => acc + (s.net_salary || 0), 0);
  const totalMyGross = myPayslips.reduce((acc, s) => acc + (s.gross_earnings || 0), 0);
  const totalMyTds = myPayslips.reduce((acc, s) => acc + (s.income_tax_tds || 0), 0);

  return (
    <div>
      <div className="page-head">
        <div>
          <span className="eyebrow">
            <Icon name="payroll" size="xs" />
            <span>Enterprise Compensation & Governance</span>
          </span>
          <h1>Payroll & Payslip Center</h1>
          <div className="muted mono" style={{ fontSize: 12, marginTop: 3 }}>
            Tenant: <strong>{session?.tenant.name}</strong> · Role: <strong>{session?.user.roles.join(', ')}</strong>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {isHRorAdmin && (
            <button className="btn-primary" onClick={() => setRunModal(true)}>
              <Icon name="refresh" size="xs" />
              <span>Run Monthly Payroll</span>
            </button>
          )}
        </div>
      </div>

      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      {!isHRorAdmin && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', background: 'var(--brand-light)', border: '1px solid rgba(var(--brand-primary-rgb, 79, 70, 229), 0.2)', borderRadius: 'var(--radius-md)', marginBottom: 16, fontSize: 12, color: 'var(--brand-primary)' }}>
          <Icon name="infoCircle" size="xs" />
          <span>You are in <strong>Employee Self-Service (ESS)</strong> mode. Company-wide administration (Run Payroll, 2-Man Approvals, Master Register) is accessible by switching to <strong>Payroll Admin</strong> or <strong>Owner</strong> in the top-right persona menu.</span>
        </div>
      )}


      {/* Navigation Tabs */}

      <div className="tabs" style={{ marginBottom: 20 }}>
        <button
          className={`tab-btn ${tab === 'mypayslips' ? 'active' : ''}`}
          onClick={() => setTab('mypayslips')}
        >
          <Icon name="receipt" size="xs" />
          <span>My Payslips (ESS)</span>
        </button>

        {isHRorAdmin && (
          <button
            className={`tab-btn ${tab === 'runs' ? 'active' : ''}`}
            onClick={() => setTab('runs')}
          >
            <Icon name="payroll" size="xs" />
            <span>Payroll Runs & Governance</span>
          </button>
        )}

        {isHRorAdmin && (
          <button
            className={`tab-btn ${tab === 'directory' ? 'active' : ''}`}
            onClick={() => setTab('directory')}
          >
            <Icon name="users" size="xs" />
            <span>HR Payslip Directory</span>
          </button>
        )}

        <button
          className={`tab-btn ${tab === 'declarations' ? 'active' : ''}`}
          onClick={() => setTab('declarations')}
        >
          <Icon name="fileSpreadsheet" size="xs" />
          <span>Tax & Statutory Declarations</span>
        </button>
      </div>


      {loading ? (
        <div className="panel"><div className="empty">Loading payroll and payslip records...</div></div>
      ) : tab === 'mypayslips' ? (
        /* =========================================================================
           1. MY PAYSLIPS (EMPLOYEE SELF SERVICE)
           ========================================================================= */
        <div>
          {/* ESS Summary Cards */}
          <div className="stats-grid" style={{ marginBottom: 20 }}>
            <div className="stat-card">
              <span className="stat-label">Latest Net Paycheck</span>
              <span className="stat-value money" style={{ color: 'var(--status-verified)' }}>
                {formatMoney(myPayslips[0]?.net_salary || 54600)}
              </span>
              <span className="stat-hint">Period: {myPayslips[0]?.month || '2026-07'} · Disbursed via Bank</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">YTD Gross Earnings</span>
              <span className="stat-value money" style={{ color: 'var(--brand-primary)' }}>
                {formatMoney(totalMyGross)}
              </span>
              <span className="stat-hint">Across {myPayslips.length} published payslips</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">YTD Tax Withheld</span>
              <span className="stat-value money" style={{ color: 'var(--status-info)' }}>
                {formatMoney(totalMyTds)}
              </span>
              <span className="stat-hint">Statutory withholdings / {jurisdiction.taxIdLabel}</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Total Net Disbursed</span>
              <span className="stat-value money" style={{ color: 'var(--ink)' }}>
                {formatMoney(totalMyNet)}
              </span>
              <span className="stat-hint">100% verified bank credits</span>
            </div>
          </div>

          <div className="panel">
            <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <strong>My Official Salary Slips</strong>
                <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                  Published and verified by Payroll Administration
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ margin: 0, fontSize: 11 }}>Financial Year:</label>
                <select
                  value={myFyFilter}
                  onChange={(e) => setMyFyFilter(e.target.value)}
                  style={{ width: 'auto', padding: '6px 12px', fontSize: 12 }}
                >
                  <option value="all">All Financial Years</option>
                  <option value="2026-2027">FY 2026-2027</option>
                  <option value="2025-2026">FY 2025-2026</option>
                </select>
              </div>
            </div>

            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Pay Period</th>
                    <th>FY</th>
                    <th>Paid Days</th>
                    <th>Gross Earnings</th>
                    <th>Total Deductions</th>
                    <th>Net Paid</th>
                    <th>Disbursement</th>
                    <th style={{ textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {myPayslips.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty">
                        No published payslips found for your account in the selected period.
                      </td>
                    </tr>
                  ) : (
                    myPayslips.map((p) => (
                      <tr key={p.id}>
                        <td className="mono"><strong>{p.month}</strong></td>
                        <td className="mono muted">{p.financial_year || '2026-2027'}</td>
                        <td className="mono">{p.paid_days} / {p.working_days} days</td>
                        <td className="mono money">{formatMoney(p.gross_earnings || 0)}</td>
                        <td className="mono money">{formatMoney(p.total_deductions || 0)}</td>
                        <td className="mono money" style={{ fontWeight: 700, color: 'var(--status-verified)' }}>
                          {formatMoney(p.net_salary || 0)}
                        </td>
                        <td><StatusBadge status={p.payment_status} /></td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn-primary btn-sm" onClick={() => void openPayslip(p.id)}>
                            <Icon name="eye" size="xs" />
                            <span>View & Download</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      ) : tab === 'runs' ? (
        /* =========================================================================
           2. PAYROLL RUNS & GOVERNANCE WORKFLOW (HR / PAYROLL ADMIN)
           ========================================================================= */
        <div>
          <div className="panel" style={{ marginBottom: 24 }}>
            <div className="panel-head">
              <div>
                <strong>Monthly Payroll Runs & Governance Cycles</strong>
                <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                  Lifecycle: <strong>1. Draft → 2. Review → 3. Finalize & Approve → 4. Publish to Staff</strong>
                </span>
              </div>
              <button className="btn-primary btn-sm" onClick={() => setRunModal(true)}>
                <Icon name="plus" size="xs" />
                <span>New Payroll Run</span>
              </button>
            </div>

            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Cycle Month</th>
                    <th>Staff Count</th>
                    <th>Gross Cost</th>
                    <th>Deductions</th>
                    <th>TDS Withheld</th>
                    <th>Net Disbursal</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Workflow Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="empty">No payroll runs found. Click "+ New Payroll Run" to start one.</td>
                    </tr>
                  ) : (
                    runs.map((r) => (
                      <tr key={r.id}>
                        <td className="mono"><strong>{r.month}</strong></td>
                        <td className="mono">{r.total_employees}</td>
                        <td className="mono money">{formatMoney(r.total_gross || 0)}</td>
                        <td className="mono money">{formatMoney(r.total_deductions || 0)}</td>
                        <td className="mono money">{formatMoney(r.total_tax_tds || 0)}</td>
                        <td className="mono money" style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>
                          {formatMoney(r.total_net_pay || 0)}
                        </td>
                        <td><StatusBadge status={r.status} /></td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button className="btn-quiet btn-sm" onClick={() => void openRunDetails(r)}>
                              <Icon name="eye" size="xs" />
                              <span>Inspect Slips</span>
                            </button>
                            {r.status === 'draft' && (
                              <button className="btn-primary btn-sm" onClick={() => void handleFinalize(r.id)}>
                                <Icon name="lock" size="xs" />
                                <span>Finalize & Lock</span>
                              </button>
                            )}
                            {(r.status === 'finalized' || r.status === 'draft') && (
                              <button
                                className="btn-success btn-sm"
                                onClick={() => void handlePublishRun(r.id)}
                                title="Publish payslips to employee ESS portals"
                              >
                                <Icon name="send" size="xs" />
                                <span>Publish to Staff</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Selected Run Payslips Breakdown */}
          {selectedRun && (
            <div className="panel">
              <div className="panel-head">
                <div>
                  <strong>Calculated Payslips for {selectedRun.month}</strong>
                  <span className="muted mono" style={{ fontSize: 12, marginLeft: 8 }}>({selectedRunPayslips.length} employees)</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-success btn-sm" onClick={() => void handlePublishRun(selectedRun.id)}>
                    <Icon name="send" size="xs" />
                    <span>Publish All {selectedRunPayslips.length} Slips</span>
                  </button>
                  <button className="btn-quiet btn-sm" onClick={() => setSelectedRun(null)}>
                    <Icon name="close" size="xs" />
                    <span>Close</span>
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Working / Paid Days</th>
                      <th>Gross</th>
                      <th>Deductions</th>
                      <th>Net Salary</th>
                      <th>Portal Status</th>
                      <th style={{ textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRunPayslips.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <strong>{s.first_name} {s.last_name}</strong>
                          <span className="muted mono" style={{ display: 'block', fontSize: 11 }}>{s.employee_code} · {s.designation || 'Staff'}</span>
                        </td>
                        <td className="mono">{s.paid_days} / {s.working_days} days</td>
                        <td className="mono money">{formatMoney(s.gross_earnings || 0)}</td>
                        <td className="mono money">{formatMoney(s.total_deductions || 0)}</td>
                        <td className="mono money" style={{ fontWeight: 700 }}>{formatMoney(s.net_salary || 0)}</td>
                        <td>
                          <StatusBadge status={s.status} />
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button className="btn-quiet btn-sm" onClick={() => void openPayslip(s.id)}>
                            <Icon name="eye" size="xs" />
                            <span>View Slip</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : tab === 'directory' ? (
        /* =========================================================================
           3. FULL PAYSLIP DIRECTORY & SEARCH
           ========================================================================= */
        <div className="panel">
          <div className="panel-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <strong>Organization-wide Payslip Directory</strong>
              <span className="muted" style={{ display: 'block', fontSize: 12 }}>
                Search, filter, inspect and resend published payslips
              </span>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                type="text"
                placeholder="Search staff code / name..."
                value={dirSearch}
                onChange={(e) => setDirSearch(e.target.value)}
                style={{ width: 180, padding: '6px 12px', fontSize: 12 }}
              />
              <input
                type="month"
                value={dirMonth}
                onChange={(e) => setDirMonth(e.target.value)}
                style={{ width: 'auto', padding: '6px 12px', fontSize: 12 }}
                title="Filter by cycle month"
              />
              <select value={dirDept} onChange={(e) => setDirDept(e.target.value)} style={{ width: 'auto', padding: '6px 12px', fontSize: 12 }}>
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <select value={dirStatus} onChange={(e) => setDirStatus(e.target.value)} style={{ width: 'auto', padding: '6px 12px', fontSize: 12 }}>
                <option value="all">All Statuses</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
              </select>

            </div>
          </div>

          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Month</th>
                  <th>Net Disbursed</th>
                  <th>Status</th>
                  <th>Delivered</th>
                  <th>Viewed</th>
                  <th>Downloaded</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {directoryPayslips.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty">No payslip records matching filter criteria.</td>
                  </tr>
                ) : (
                  directoryPayslips.map((ps) => (
                    <tr key={ps.id}>
                      <td>
                        <strong>{ps.first_name} {ps.last_name}</strong>
                        <span className="muted mono" style={{ display: 'block', fontSize: 11 }}>{ps.employee_code}</span>
                      </td>
                      <td>{ps.department_name || 'General'}</td>
                      <td className="mono">{ps.month}</td>
                      <td className="mono money" style={{ fontWeight: 700 }}>{formatMoney(ps.net_salary || 0)}</td>
                      <td>
                        <StatusBadge status={ps.status} />
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {ps.sent_at ? (
                          <span style={{ color: 'var(--status-verified)' }}>✓ {new Date(ps.sent_at).toLocaleDateString()}</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {ps.viewed_at ? (
                          <span style={{ color: 'var(--brand-primary)' }}>👁 {new Date(ps.viewed_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        ) : (
                          <span className="muted">Unread</span>
                        )}
                      </td>
                      <td className="mono" style={{ fontSize: 11 }}>
                        {ps.downloaded_at ? (
                          <span style={{ color: 'var(--status-verified)' }}>Downloaded</span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn-quiet btn-sm" onClick={() => void openPayslip(ps.id)}>
                            <Icon name="eye" size="xs" />
                            <span>View</span>
                          </button>
                          <button
                            className="btn-quiet btn-sm"
                            onClick={() => void handleShareSlip(ps.id)}
                            title="Send/Resend notification"
                          >
                            <Icon name="send" size="xs" />
                            <span>Send</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* =========================================================================
           4. TAX & COMPLIANCE DECLARATIONS
           ========================================================================= */
        <div className="panel" style={{ maxWidth: 700, margin: '0 auto', padding: 24 }}>
          <h2 style={{ marginBottom: 4 }}>Tax & Compliance Declaration ({jurisdiction.countryCode})</h2>
          <p className="muted" style={{ marginTop: 0, marginBottom: 20 }}>
            Declare investments and filing regime under {jurisdiction.countryName} statutory law ({jurisdiction.accountingStandard}) for accurate payroll withholdings.
          </p>

          <form onSubmit={handleSaveTax}>
            <div className="form-grid">
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Fiscal Year</label>
                  <select value={fy} onChange={(e) => setFy(e.target.value)}>
                    <option value="2026-2027">FY 2026-2027</option>
                    <option value="2025-2026">FY 2025-2026</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Withholding / Filing Regime</label>
                  <select value={regime} onChange={(e) => setRegime(e.target.value as any)}>
                    {jurisdiction.filingRegimes.map((r) => (
                      <option key={r.key} value={r.key}>{r.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {country === 'IN' && regime === 'old' && (
                <div className="form-grid" style={{ padding: 14, background: 'var(--bg-field)', borderRadius: 6 }}>
                  <div className="form-group">
                    <label>Section 80C Investments (EPF, PPF, ELSS - Max ₹1,50,000)</label>
                    <input type="number" min="0" max="150000" value={sec80c} onChange={(e) => setSec80c(Number(e.target.value))} />
                  </div>
                  <div className="form-group">
                    <label>Section 80D Health Insurance Premium</label>
                    <input type="number" min="0" value={sec80d} onChange={(e) => setSec80d(Number(e.target.value))} />
                  </div>
                  <div className="form-group">
                    <label>House Rent Allowance (HRA) - Annual Rent</label>
                    <input type="number" min="0" value={rentPaid} onChange={(e) => setRentPaid(Number(e.target.value))} />
                  </div>
                  <div className="form-group">
                    <label>Other Exemptions (NPS 80CCD, Loan 80E)</label>
                    <input type="number" min="0" value={otherExemptions} onChange={(e) => setOtherExemptions(Number(e.target.value))} />
                  </div>
                </div>
              )}

              <div className="banner info" style={{ margin: 0 }}>
                Operating under <strong>{jurisdiction.countryName}</strong> statutory regime ({jurisdiction.accountingStandard}). Withholdings calculated automatically.
              </div>

              <div style={{ marginTop: 10 }}>
                <button type="submit" className="btn-primary" style={{ width: '100%' }}>
                  Save Tax Declaration
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* Execute Run Modal */}
      {runModal && (
        <div className="modal-backdrop" onClick={() => setRunModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <span className="eyebrow">Payroll Processing</span>
                <h3>Execute Monthly Payroll Run</h3>
              </div>
              <button className="btn-icon" onClick={() => setRunModal(false)} aria-label="Close">
                <Icon name="close" size="sm" />
              </button>
            </div>
            <form onSubmit={handleExecuteRun}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="form-group">
                  <label>Payroll Month (YYYY-MM)</label>
                  <input type="month" required value={runMonth} onChange={(e) => setRunMonth(e.target.value)} />
                </div>
                <div className="banner info">
                  This calculates earnings, loss-of-pay (attendance & unapproved leaves), PF (12%), ESI (0.75%), Professional Tax, loan EMIs, and TDS for all active employees.
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" className="btn-quiet" onClick={() => setRunModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={processingRun}>
                  {processingRun ? 'Computing Run...' : 'Generate Draft Run'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =========================================================================
         5. DETAILED OFFICIAL PAYSLIP MODAL
         ========================================================================= */}
      {viewingSlipId && (
        <div className="modal-backdrop" onClick={closePayslip}>
          <div className="modal-content" style={{ maxWidth: 780, padding: 24, borderRadius: 10 }} onClick={(e) => e.stopPropagation()}>
            <div className="print-area">
              {loadingSlip || !slipDetail ? (
                <div className="empty">Loading verified payslip details...</div>
              ) : (
                <div>
                  {/* Letterhead Header with Legal Entity Details */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid var(--brand-primary)', paddingBottom: 16, marginBottom: 16 }}>
                    <div>
                      <div className="eyebrow" style={{ color: 'var(--brand-primary)' }}>Official Compensation Statement</div>
                      <h2 style={{ margin: '2px 0 0 0', fontSize: 20, fontWeight: 800 }}>
                        {slipDetail.payslip.organization_name || jurisdiction.sampleOrgName}
                      </h2>
                      <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                        {jurisdiction.sampleAddress}
                      </div>
                      <div className="mono muted" style={{ fontSize: 11, marginTop: 2 }}>
                        {jurisdiction.taxIdLabel}: <strong>{jurisdiction.taxIdPlaceholder}</strong> · Period: <strong>{slipDetail.payslip.month}</strong>
                      </div>
                    </div>

                    <div style={{ textAlign: 'right' }}>
                      <StatusBadge status={slipDetail.payslip.status} />
                      <div className="mono muted" style={{ fontSize: 11, marginTop: 6 }}>
                        Ref: {slipDetail.payslip.payment_reference || `PAY-${slipDetail.payslip.id.slice(0, 8)}`}
                      </div>
                    </div>
                  </div>

                  {/* Employee & Bank Info Matrix */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, background: 'var(--bg-field)', padding: 14, borderRadius: 6, marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-secondary)', fontWeight: 700 }}>Employee Information</div>
                      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>{slipDetail.payslip.first_name} {slipDetail.payslip.last_name}</div>
                      <div className="mono muted" style={{ fontSize: 12 }}>Code: <strong>{slipDetail.payslip.employee_code}</strong> · Role: {slipDetail.payslip.designation || 'Staff'}</div>
                    </div>

                    <div>
                      <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--ink-secondary)', fontWeight: 700 }}>Attendance & Banking</div>
                      <div className="mono" style={{ fontSize: 13 }}>
                        Paid Days: <strong>{slipDetail.payslip.paid_days}</strong> / {slipDetail.payslip.working_days} (LOP: {slipDetail.payslip.loss_of_pay_days})
                      </div>
                      {slipDetail.maskedBank ? (
                        <div className="mono muted" style={{ fontSize: 11, marginTop: 2 }}>
                          Bank: {slipDetail.maskedBank.bankName || 'Bank'} · A/C: {slipDetail.maskedBank.accountNumber} ({slipDetail.maskedBank.ifsc || 'Routing/IFSC'})
                        </div>
                      ) : (
                        <div className="mono muted" style={{ fontSize: 11, marginTop: 2 }}>Bank details verified</div>
                      )}
                    </div>
                  </div>

                  {/* Earnings vs Deductions Breakdown */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                    {/* Earnings Table */}
                    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ padding: '8px 12px', background: 'var(--bg-field)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>
                        Earnings ({jurisdiction.currencySymbol})
                      </div>
                      <table style={{ margin: 0, fontSize: 12 }}>
                        <tbody>
                          {slipDetail.lineItems.filter((i) => i.type === 'earning').map((item) => (
                            <tr key={item.id}>
                              <td>{item.component_name}</td>
                              <td className="mono money" style={{ textAlign: 'right' }}>{formatMoney(item.amount)}</td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: 700, background: 'var(--bg-surface)', borderTop: '2px solid var(--border-subtle)' }}>
                            <td>Gross Earnings</td>
                            <td className="mono money" style={{ textAlign: 'right', color: 'var(--brand-primary)' }}>
                              {formatMoney(slipDetail.payslip.gross_earnings)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {/* Deductions Table */}
                    <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{ padding: '8px 12px', background: 'var(--bg-field)', fontWeight: 700, fontSize: 12, textTransform: 'uppercase' }}>
                        Deductions ({jurisdiction.currencySymbol})
                      </div>
                      <table style={{ margin: 0, fontSize: 12 }}>
                        <tbody>
                          {slipDetail.lineItems.filter((i) => i.type === 'deduction').map((item) => (
                            <tr key={item.id}>
                              <td>{item.component_name}</td>
                              <td className="mono money" style={{ textAlign: 'right' }}>{formatMoney(item.amount)}</td>
                            </tr>
                          ))}
                          <tr style={{ fontWeight: 700, background: 'var(--bg-surface)', borderTop: '2px solid var(--border-subtle)' }}>
                            <td>Total Deductions</td>
                            <td className="mono money" style={{ textAlign: 'right', color: 'var(--status-alert)' }}>
                              {formatMoney(slipDetail.payslip.total_deductions)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Net Disbursable Strip */}
                  <div style={{ padding: '16px 20px', background: 'var(--bg-field)', border: '1px solid var(--border-subtle)', borderRadius: 6, marginBottom: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span className="eyebrow" style={{ color: 'var(--status-verified)' }}>Net Take-Home Salary</span>
                        <div className="mono money" style={{ fontSize: 24, fontWeight: 800, color: 'var(--status-verified)' }}>
                          {formatMoney(slipDetail.payslip.net_salary)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span className="muted" style={{ fontSize: 11 }}>Amount in Words:</span>
                        <div style={{ fontSize: 13, fontWeight: 600, fontStyle: 'italic' }}>
                          {formatAmountInWords(slipDetail.payslip.net_salary, jurisdiction.currency)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="muted" style={{ fontSize: 11, textAlign: 'center', borderTop: '1px dashed var(--border-subtle)', paddingTop: 10 }}>
                    This is a computer-generated official payslip issued by {slipDetail.payslip.organization_name || jurisdiction.sampleOrgName}. Authorized and tamper-evident under system audit log.
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 20, paddingTop: 14, borderTop: '1px solid var(--border-subtle)' }}>
              <div className="muted mono" style={{ fontSize: 11 }}>
                {slipDetail?.payslip.viewed_at && `Viewed: ${new Date(slipDetail.payslip.viewed_at).toLocaleString()}`}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                {isHRorAdmin && slipDetail && (
                  <button
                    type="button"
                    className="btn-quiet"
                    disabled={sharing}
                    onClick={() => void handleShareSlip(slipDetail.payslip.id)}
                  >
                    <Icon name="send" size="xs" />
                    <span>{sharing ? 'Sending...' : 'Resend Notification'}</span>
                  </button>
                )}

                {slipDetail && (
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void handlePrintAndDownload(slipDetail.payslip.id)}
                  >
                    <Icon name="printer" size="xs" />
                    <span>Print / Download PDF</span>
                  </button>
                )}

                <button type="button" className="btn-quiet" onClick={() => { setViewingSlipId(null); setSlipDetail(null); }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
